// Nano store implementation

/**
 * Create a reactive store
 * @param {*} initialValue - Initial state value
 * @returns {Object} Store with get, set, subscribe methods
 */
export function createStore(initialValue) {
  let _value = initialValue;
  const _subscribers = new Set();

  return {
    /**
     * Get current value
     * @returns {*}
     */
    get() {
      return _value;
    },

    /**
     * Set new value
     * @param {*|Function} updater - New value or updater function
     */
    set(updater) {
      const newValue = typeof updater === 'function' ? updater(_value) : updater;
      if (newValue !== _value) {
        const oldValue = _value;
        _value = newValue;
        _subscribers.forEach((fn) => fn(newValue, oldValue));
      }
    },

    /**
     * Subscribe to changes
     * @param {Function} callback - Callback(newValue, oldValue)
     * @returns {Function} Unsubscribe function
     */
    subscribe(callback) {
      _subscribers.add(callback);
      // Call immediately with current value
      callback(_value, undefined);
      return () => _subscribers.delete(callback);
    },

    /**
     * Reset to initial value
     */
    reset() {
      this.set(initialValue);
    },
  };
}

/**
 * Create a map store (object state)
 * @param {Object} initialValue - Initial state object
 * @returns {Object} Store with get, set, setKey, subscribe methods
 */
export function createMapStore(initialValue = {}) {
  const store = createStore(initialValue);

  return {
    ...store,

    /**
     * Set a specific key
     * @param {string} key
     * @param {*} value
     */
    setKey(key, value) {
      store.set((prev) => ({ ...prev, [key]: value }));
    },

    /**
     * Get a specific key
     * @param {string} key
     * @returns {*}
     */
    getKey(key) {
      return store.get()[key];
    },
  };
}
